import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import { Search, ChevronLeft, ChevronRight, Loader2, Calendar, DollarSign, ArrowUpCircle, ArrowDownCircle } from "lucide-react";

interface InvestmentHistory {
  _id: string;
  email: string;
  totalAmount: string;
  amount: string;
  subscription: string;
  incomeDate?: string;
  investDate?: string;
  action: string;
  type: string;
  actionDate?: string;
  createdAt: string;
}

const InvestmentHistory = () => {
  const [historyData, setHistoryData] = useState<InvestmentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/invest/admin/invest-history`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm
        }
      });

      if (response.data.status === "success") {
        setHistoryData(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error("Error fetching investment history:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchHistory();
    }, 500);
    return () => clearTimeout(delaySearch);
  }, [fetchHistory]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Investment History</h1>
          <p className="text-sm md:text-base text-gray-500 mt-1">Audit trail of all investment credits and debits</p>
        </div>

        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by email..."
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-sm md:text-base"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            <p className="text-gray-500 font-medium mt-4">Loading history...</p>
          </div>
        ) : historyData.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <Search className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-500 font-medium">No history found</p>
          </div>
        ) : (
          historyData.map((history) => (
            <div key={history._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 flex flex-col gap-4">
              {/* Header */}
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{history.email}</h3>
                  <p className="text-[10px] text-gray-400 mt-0.5">{new Date(history.createdAt).toLocaleDateString('en-GB')} • {history.action}</p>
                </div>
                <span className={`shrink-0 px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-tight ${history.type === 'credit'
                  ? 'bg-green-50 text-green-600 border border-green-100'
                  : 'bg-red-50 text-red-600 border border-red-100'
                  }`}>
                  {history.type === 'credit' ? 'Deposit' : 'Withdraw'}
                </span>
              </div>

              {/* Info Grid - 2 Columns on mobile */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 text-xs">
                <div>
                  <span className="block text-[9px] text-gray-400 uppercase font-bold mb-0.5">Subscription</span>
                  <span className="text-gray-700 font-medium">{history.subscription}</span>
                </div>
                <div>
                  <span className="block text-[9px] text-gray-400 uppercase font-bold mb-0.5">Invested Date</span>
                  <span className="text-gray-700 font-medium">{history.investDate ? new Date(history.investDate).toLocaleDateString('en-GB') : 'N/A'}</span>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <span className="block text-[9px] text-gray-400 uppercase font-bold mb-1">Amounts</span>
                  <div className="flex items-center gap-3">
                    <div className="bg-gray-50 px-2 py-1 rounded">
                      <span className="text-[9px] text-gray-400 block">Total</span>
                      <span className="text-gray-900 font-bold">${parseFloat(history.totalAmount).toLocaleString()}</span>
                    </div>
                    <div className={`${history.type === 'credit' ? 'bg-green-50' : 'bg-red-50'} px-2 py-1 rounded`}>
                      <span className="text-[9px] text-gray-400 block">Change</span>
                      <span className={`${history.type === 'credit' ? 'text-green-600' : 'text-red-600'} font-black text-sm`}>
                        {history.type === 'credit' ? '+' : '-'}${parseFloat(history.amount).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Pagination */}
        {!loading && historyData.length > 0 && (
          <div className="p-4 md:px-6 md:py-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500 order-2 md:order-1">
              Showing <span className="font-bold text-gray-900">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
              <span className="font-bold text-gray-900">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{" "}
              of <span className="font-bold text-gray-900">{pagination.total}</span> entries
            </p>

            <div className="flex items-center gap-2 md:gap-3 order-1 md:order-2">
              <span className="text-sm text-gray-600 hidden sm:inline">Show:</span>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  const newLimit = parseInt(e.target.value, 10);
                  setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
                }}
                className="border border-gray-300 rounded-lg px-3 py-1 text-sm bg-white"
              >
                <option value="20">20 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
              </button>

              <div className="flex items-center gap-1 md:gap-2">
                {[...Array(pagination.totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.totalPages ||
                    (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                        className={`w-8 h-8 md:w-10 md:h-10 text-xs md:text-sm font-bold rounded-lg transition-all shadow-sm ${pagination.page === pageNum
                          ? "bg-blue-600 text-white shadow-blue-200/50"
                          : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === pagination.page - 2 ||
                    pageNum === pagination.page + 2
                  ) {
                    return <span key={pageNum} className="text-gray-400 text-xs">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestmentHistory;
